import React, { useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import Card from '../../components/ui/Card';
import Avatar from '../../components/ui/Avatar';
import Badge from '../../components/ui/Badge';
import Table from '../../components/ui/Table';
import { analyticsData, employees, stores } from '../../data/mockData';

const formatMoney = v => (v / 1000000).toFixed(1) + ' млн сум';

export default function Analytics() {
  const [view, setView] = useState('summary');
  const [period, setPeriod] = useState('month');

  const byEmployeeData = analyticsData.byEmployee.map(e => ({
    ...e,
    employeeName: employees.find(emp => emp.id === e.employeeId)?.name || e.employeeId,
  }));

  const byStoreData = analyticsData.byStore.map(s => ({
    ...s,
    storeName: stores.find(st => st.id === s.storeId)?.name || s.storeId,
  }));

  const employeeColumns = [
    { key: 'employeeName', label: 'Сотрудник', render: (v) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Avatar name={v} size={30} />
        <span style={{ fontWeight: 600 }}>{v}</span>
      </div>
    )},
    { key: 'tasksCompleted', label: 'Задач', render: v => <span style={{ fontWeight: 700, color: '#4A6CF7' }}>{v}</span> },
    { key: 'osgChecks', label: 'ОСГ проверок', render: v => <Badge variant="osg">{v}</Badge> },
    { key: 'displays', label: 'Выкладок', render: v => <Badge variant="display">{v}</Badge> },
    { key: 'scans', label: 'Сканирований' },
    { key: 'avgTime', label: 'Ср. время', render: v => <span>{v} мин</span> },
    { key: 'rating', label: 'Рейтинг', render: v => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 6, background: '#F3F4F6', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ width: `${v}%`, height: '100%', background: v >= 80 ? '#34C759' : v >= 60 ? '#4A6CF7' : '#FF9500', borderRadius: 99 }} />
        </div>
        <span style={{ fontWeight: 700, color: '#1A1A2E', minWidth: 32 }}>{v}</span>
      </div>
    )},
  ];

  const storeColumns = [
    { key: 'storeName', label: 'Магазин', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'tasksCompleted', label: 'Задач', render: v => <span style={{ fontWeight: 700, color: '#4A6CF7' }}>{v}</span> },
    { key: 'osgChecks', label: 'ОСГ проверок', render: v => <Badge variant="osg">{v}</Badge> },
    { key: 'displays', label: 'Выкладок', render: v => <Badge variant="display">{v}</Badge> },
    { key: 'scans', label: 'Сканирований' },
    { key: 'avgTime', label: 'Ср. время', render: v => <span>{v} мин</span> },
  ];

  const tabs = [
    { key: 'summary', label: 'Сводка' },
    { key: 'employees', label: 'По сотрудникам' },
    { key: 'stores', label: 'По магазинам' },
    { key: 'categories', label: 'По категориям' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E' }}>Аналитика</h1>
          <p style={{ color: '#8E8E93', fontSize: 13, marginTop: 2 }}>Данные по всей сети</p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['day','week','month','custom'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: '7px 14px', borderRadius: 8, border: '1px solid #E5E7EB',
              background: period === p ? '#4A6CF7' : '#fff', color: period === p ? '#fff' : '#6B7280',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>{{ day: 'День', week: 'Неделя', month: 'Месяц', custom: 'Кастом' }[p]}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, background: '#fff', padding: '6px', borderRadius: 12, width: 'fit-content', border: '1px solid #F0F0F0' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setView(t.key)} style={{
            padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: view === t.key ? 700 : 500,
            background: view === t.key ? '#4A6CF7' : 'transparent',
            color: view === t.key ? '#fff' : '#6B7280', fontFamily: 'inherit',
          }}>{t.label}</button>
        ))}
      </div>

      {view === 'summary' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            {[
              { label: 'ОСГ проверок', value: analyticsData.kpi.stockChecksCompleted, color: '#FF6B6B', bg: '#FFF0F0' },
              { label: 'Выкладок завершено', value: analyticsData.kpi.displayCompleted, color: '#FFBE0B', bg: '#FFFBEB' },
              { label: '% выполнения задач', value: `${analyticsData.kpi.completionRate}%`, color: '#34C759', bg: '#F0FFF4' },
              { label: 'Сканирований', value: analyticsData.kpi.totalScans, color: '#4A6CF7', bg: '#EEF2FF' },
            ].map(k => (
              <div key={k.label} style={{ background: k.bg, borderRadius: 12, padding: '16px 20px' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: k.color }}>{k.value}</div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{k.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
            <Card title="Тренд ОСГ проверок и выкладок (30 дней)">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={analyticsData.dailyData.slice(-14)} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="osgChecks" stroke="#FF6B6B" strokeWidth={2} dot={false} name="ОСГ" />
                  <Line type="monotone" dataKey="displays" stroke="#FFBE0B" strokeWidth={2} dot={false} name="Выкладки" />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card title="Финансовая сводка">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { label: 'Стоимость товаров', value: formatMoney(analyticsData.financial.totalOriginalValue), color: '#4A6CF7' },
                  { label: 'Потери (списание)', value: formatMoney(analyticsData.financial.totalWriteOffValue), color: '#FF3B30' },
                  { label: 'Коэффициент потерь', value: `${analyticsData.financial.lossRate}%`, color: '#FF9500' },
                ].map(f => (
                  <div key={f.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#F8F9FB', borderRadius: 8 }}>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>{f.label}</span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: f.color }}>{f.value}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {view === 'employees' && (
        <Card title="Рейтинг сотрудников по эффективности" padding="0">
          <Table columns={employeeColumns} data={byEmployeeData} emptyText="Нет данных" />
        </Card>
      )}

      {view === 'stores' && (
        <Card title="Статистика по магазинам" padding="0">
          <Table columns={storeColumns} data={byStoreData} emptyText="Нет данных" />
        </Card>
      )}

      {view === 'categories' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card title="Распределение по категориям">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={analyticsData.byCategory} margin={{ top: 5, right: 20, bottom: 60, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                <XAxis dataKey="categoryName" tick={{ fontSize: 11, fill: '#9CA3AF' }} angle={-35} textAnchor="end" />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="markdownCount" name="Списаний" fill="#4A6CF7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card padding="0">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F8F9FB' }}>
                  {['Категория', 'Списаний', 'Стоимость', 'Доля'].map(h => (
                    <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #F0F0F0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {analyticsData.byCategory.map((c, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>{c.categoryName}</td>
                    <td style={{ padding: '12px 16px' }}><Badge variant="osg">{c.markdownCount}</Badge></td>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#4A6CF7' }}>{formatMoney(c.totalValue)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: '#F3F4F6', borderRadius: 99, overflow: 'hidden' }}>
                          <div style={{ width: `${c.percentage}%`, height: '100%', background: '#4A6CF7', borderRadius: 99 }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, minWidth: 40 }}>{c.percentage}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
}
